import { FC, ReactNode } from 'react';

import { ArrowRight, ShoppingCart, Store } from 'lucide-react';

import { Card, CardContent } from '../../../components/ui/Card';
import { cn } from '../../../utils';

type RoleCardProps = {
    href: string;
    label: string;
    title: string;
    icon: ReactNode;
    variant: 'buyer' | 'seller';
};

const variants = {
    buyer: {
        card: 'border-blue-200 group-hover:border-blue-400 dark:border-blue-900 dark:group-hover:border-blue-700',
        icon: 'bg-blue-500/10 text-blue-700 dark:text-blue-300',
        label: 'text-blue-700 dark:text-blue-300',
    },
    seller: {
        card: 'border-emerald-200 group-hover:border-emerald-400 dark:border-emerald-900 dark:group-hover:border-emerald-700',
        icon: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
        label: 'text-emerald-700 dark:text-emerald-300',
    },
};

const RoleCard: FC<RoleCardProps> = ({ href, label, title, icon, variant }) => {
    const colors = variants[variant];

    return (
        <a
            href={href}
            className="group rounded-2xl focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none"
        >
            <Card className={cn('h-full transition-colors', colors.card)}>
                <CardContent className="flex h-full items-center gap-4 p-5 sm:p-6">
                    <div
                        className={cn(
                            'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl',
                            colors.icon,
                        )}
                    >
                        {icon}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className={cn('text-sm font-medium', colors.label)}>
                            {label}
                        </p>
                        <h3 className="mt-1 text-lg font-semibold">{title}</h3>
                    </div>
                    <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1" />
                </CardContent>
            </Card>
        </a>
    );
};

export const FaqRoleSelector: FC = () => (
    <section className="mt-10">
        <h2 className="mb-5 text-xl font-semibold">З чого почати?</h2>

        <div className="grid gap-4 md:grid-cols-2">
            <RoleCard
                href="#buyers"
                label="Я покупець"
                title="Хочу знайти й купити товар"
                icon={<ShoppingCart className="h-6 w-6" />}
                variant="buyer"
            />
            <RoleCard
                href="#sellers"
                label="Я продавець"
                title="Хочу виставити товар на продаж"
                icon={<Store className="h-6 w-6" />}
                variant="seller"
            />
        </div>
    </section>
);
